import React, { useEffect, useRef, useState } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase, supabaseHelpers } from '@/lib/supabase'
import { logActivity } from '@/lib/activity-logger'
import { resolveRoute, buildWorkspaceUrl, buildAdminUrl } from '@/lib/workspace'
import { AuthContext } from '@/contexts/AuthContext'
import { queryClient } from '@/lib/queryClient'

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const lastLoggedToken = useRef<string | null>(null)

  useEffect(() => {
    let mounted = true

    // Get initial session
    const getInitialSession = async () => {
      try {
        // Guard against a hung getSession() — supabase-js v2 routes this
        // through the Web Locks auth lock. If that lock is contended (e.g. a
        // deadlocked onAuthStateChange callback, a stuck token refresh) the
        // call never settles, `loading` stays `true`, and every gated route
        // spins on "Loading..." forever. Failing after 8s lets the app fall
        // through to the login screen instead of hanging indefinitely.
        const sessionPromise = supabase.auth.getSession()
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession timed out')), 8000),
        )
        const { data: { session }, error } = await Promise.race([sessionPromise, timeout])

        if (error) {
          console.error('Error getting initial session:', error)
          setError('Failed to restore session. Please sign in again.')
        }

        if (mounted) {
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
        }
      } catch (err) {
        console.error('Unexpected error getting session:', err)
        if (mounted) {
          setError('An unexpected error occurred. Please refresh the page.')
          setLoading(false)
        }
      }
    }

    getInitialSession()

    // Listen for auth changes.
    //
    // IMPORTANT: this callback runs while supabase-js holds the Web Locks
    // auth lock. Calling any awaited `supabase.*` method directly inside it
    // re-enters that lock and deadlocks — a documented supabase-js footgun.
    // The callback below stays fully synchronous; every Supabase touch
    // (logActivity, the post-login profile lookup) is deferred via
    // `setTimeout(…, 0)` so it runs after the lock is released.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return

      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Clear errors on successful auth state changes / sign out
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_OUT') {
        setError(null)
      }

      // Log auth events to activity_log and route post-login cross-subdomain.
      // Deferred out of the auth-lock critical section (see note above).
      if (event === 'SIGNED_IN' && session?.user) {
        const token = session.access_token
        if (lastLoggedToken.current === token) return  // same token = background refresh, skip
        lastLoggedToken.current = token
        const signedInUser = session.user
        setTimeout(() => {
          if (!mounted) return

          const accountId = (signedInUser.app_metadata as { account_id?: string })?.account_id ||
            (signedInUser.user_metadata as { account_id?: string })?.account_id
          if (accountId) {
            logActivity({
              accountId,
              type: 'user_login',
              description: `User logged in (${signedInUser.email})`,
              metadata: { email: signedInUser.email },
            })
          }

          // Post-login cross-subdomain routing (Phase 1):
          // - On `app.icrop.net` (target.kind === 'admin'):
          //     super_admin → stay (LoginForm navigates to /dashboard, ProtectedRoute
          //     then bounces to /admin if needed).
          //     non-super_admin → redirect to <slug>.icrop.net/dashboard.
          // - On `<slug>.icrop.net` (target.kind === 'workspace'): no redirect; the
          //   workspace LoginForm handles the in-shell navigation.
          void (async () => {
            try {
              const target = resolveRoute()
              if (target.kind === 'admin') {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('role, account_id, account:accounts(slug)')
                  .eq('id', signedInUser.id)
                  .maybeSingle()
                if (profile && profile.role !== 'super_admin') {
                  const slug = (profile as { account?: { slug?: string } }).account?.slug
                  if (slug) {
                    window.location.assign(buildWorkspaceUrl(slug, '/dashboard'))
                  } else {
                    // profile has no usable account_id / slug — land on /no-account
                    // in the admin shell so the user gets a friendly message +
                    // sign-out.
                    window.location.assign(buildAdminUrl('/no-account'))
                  }
                }
              }
            } catch (redirectErr) {
              console.error('Post-login redirect resolution failed:', redirectErr)
            }
          })()
        }, 0)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      setError(null)
      await supabaseHelpers.signInWithPassword(email, password)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email: string, password: string, metadata?: Record<string, unknown>) => {
    try {
      setLoading(true)
      setError(null)
      await supabaseHelpers.signUpWithPassword(email, password, metadata)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign up'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      setError(null)
      await supabaseHelpers.signOut()
      // Purge every cached react-query entry. Query keys like
      // ['account-children', accountId] / ['organizations', accountId] are
      // scoped by account, NOT by user, so on a shared browser the next user to
      // sign in (within the 5-min staleTime) would otherwise be served the
      // previous user's cached rows — which RLS/visible_org_ids would deny.
      queryClient.clear()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string, redirectTo?: string) => {
    try {
      setError(null)
      await supabaseHelpers.resetPassword(email, redirectTo)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password'
      setError(errorMessage)
      throw err
    }
  }

  const clearError = () => {
    setError(null)
  }

  const value = {
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
